// SYNC: copy of admin/service/src/constants/roles.ts — keep 1:1
/**
 * 7 个预置角色 × 权限矩阵
 *
 * 跨 stream 接口契约 §6.5
 * 参考：project-docs/specs/2026-05-23-admin-platform-plan.md § 6.5
 *       project-docs/specs/2026-10-15-ops-lead-role-design.md §3（运营主管 ops_lead）
 *
 * web 侧 admin/web/src/contracts/roles.ts 是逐字镜像（仅多首行 `// SYNC:`），改这里必须同步改它；
 * tests/unit/contracts-mirror-sync.test.ts 会在两边不一致时失败。
 */

import { PERMISSIONS as P } from './permissions';
import type { Permission } from './permissions';

export type AdminRole =
  | 'super_admin'
  | 'operator'
  | 'support'
  | 'finance'
  | 'observer'
  | 'sales'
  | 'ops_lead';

export const ROLE_PERMISSIONS: Record<AdminRole, Permission[]> = {
  super_admin: Object.values(P),

  operator: [
    P.ORDER_READ, P.ORDER_UPDATE_LOGISTICS,
    P.AI_TASK_READ, P.AI_TASK_RETRY, P.AI_TASK_CANCEL,
    P.CONTENT_READ, P.CONTENT_AUDIT,
    P.PROMPT_READ, P.PROMPT_PUBLISH, P.PROMPT_ROLLBACK,
    P.PACKAGE_READ, P.PACKAGE_UPDATE, P.PACKAGE_PUBLISH,
    P.BOOST_READ, P.BOOST_READ_RELATIONSHIPS, P.BOOST_FREEZE, P.BOOST_AUDIT,
    P.VOUCHER_READ, P.VOUCHER_GRANT, P.VOUCHER_REVOKE,
    // 兑换码明文列表从 voucher:read 拆出：operator 本就能生成兑换码，也要能看自己生成的码
    P.REDEEM_CODE_READ,
    P.SHARE_READ, P.SHARE_REVOKE,
    P.USER_READ,
    P.PII_READ_PHONE, P.PII_READ_ADDRESS,
    P.CONFIG_READ,
    P.NOTIFICATION_SEND,
    P.DASHBOARD_READ,
    P.TICKET_READ, P.TICKET_HANDLE,
    // 只读：可看合伙人与业绩，但建档 / 停止（partner:create / stop）与补录归因（partner:attribute）都不给
    P.PARTNER_READ, P.COMMISSION_READ, P.SETTLEMENT_READ,
  ],

  support: [
    P.ORDER_READ, P.ORDER_UPDATE_LOGISTICS, P.ORDER_REVERT_SUBSTITUTE,
    P.REFUND_INITIATE,
    P.AI_TASK_READ, P.AI_TASK_RETRY,
    P.CONTENT_READ, P.CONTENT_ROLLBACK,
    P.SHARE_READ,
    P.USER_READ, P.USER_RESTORE,
    P.PII_READ_PHONE,
    P.BOOST_READ_RELATIONSHIPS,
    P.NOTIFICATION_SEND,
    P.TICKET_READ, P.TICKET_HANDLE,
  ],

  finance: [
    P.ORDER_READ,
    P.REFUND_APPROVE, P.REFUND_EXECUTE,
    P.VOUCHER_READ,
    P.DASHBOARD_READ,
    P.RECONCILIATION_READ, P.RECONCILIATION_UPLOAD, P.RECONCILIATION_MARK,
    // 对账口径要能看到佣金应发额；结算与打款是财务本职
    P.PARTNER_READ, P.COMMISSION_READ,
    P.SETTLEMENT_READ, P.SETTLEMENT_PAY,
  ],

  // 只读观察员：给尚未签约的合作方看经营全貌。铁律：
  //   - 不含任何写权限（后端每个写接口都挂的是写权限点，持读权限点碰不到）
  //   - 不含任何 pii:*：手机号一律脱敏（合伙人模块见 partners.routes 的 redact）
  //   - 不含 prompt:*（核心资产）、audit_log:read、admin:*、ticket:*（工单含联系方式）
  //   - 不含 redeem_code:read：兑换码明文列表是「谁拿到就能兑」的凭据，不是经营看板数据
  observer: [
    P.DASHBOARD_READ,
    P.ORDER_READ,
    P.AI_TASK_READ,
    P.PACKAGE_READ,
    P.BOOST_READ,
    P.VOUCHER_READ,
    P.SHARE_READ,
    P.USER_READ,
    P.CONFIG_READ,
    P.PARTNER_READ, P.COMMISSION_READ, P.SETTLEMENT_READ,
  ],

  // 销售（顾问陪跑交付）：只碰名下客户。铁律：
  //   - 只给 advisor:* 点位，名下范围由服务端按 advisor_admin_id 强制过滤
  //     （middleware/advisor-scope.ts）；越权一律 404，不暴露「这个客户存在」
  //   - 不含 order:* / user:read / pii:* / voucher:*：这些是全局点位，给了就能看全部
  //     用户、给任意订单发货、发券送书。名下客户的手机号 / 地址 / 物流走 advisor 路由
  //   - 不含 advisor:manage（看全部 / 转交 / 金额区间 / 交叉佣金判定），也不含
  //     partner:* —— 销售看不到合伙人归属详情
  //   - 没有「标记已付款 / 直接开通权益」这类点位：开书只能由支付回调触发
  sales: [
    P.ADVISOR_CUSTOMER_READ,
    P.ADVISOR_CUSTOMER_WRITE,
    P.ADVISOR_ORDER_CREATE,
    P.ADVISOR_FULFILL,
    P.ADVISOR_CONTENT_READ,
  ],

  // 运营主管（合作方主导运营时用，设计见 2026-10-15-ops-lead-role-design.md §3.1）：
  // 在 operator 的日常运营基础上，收回核心资产与钱的闸门，再加「带客户 / 招销售 / 合伙人建档」。
  // 一个账号一个角色，所以他从 sales 切过来后仍要能带客户——名下 5 个 advisor:* 点位与 sales 同。
  //   - 收回（相对 operator）：prompt:*（核心资产，详情接口返回提示词全文）、package:update/publish（改价 / 上下架）、
  //     voucher:grant（直接发券，点名券改走 voucher:request → 超管 voucher:approve）、
  //     content:audit / notification:send（两个没有任何接口的空点位，不给）
  //   - 明确不给：admin:* · audit_log:read · config:update/approve · redeem_code:read · partner:attribute（碰钱）·
  //     advisor:manage（看全部客户详情 / 一键转交全部）· refund:* · reconciliation:* · settlement:pay ·
  //     pii:read_content / pii:read_l5 · user:freeze / restore · content:rollback · freeze:revert ·
  //     lifecycle:force_purge_judicial · order:revert_substitute · voucher:approve
  //   - 数据范围：持 advisor:* 名下点位、不持 advisor:manage → 带客户时范围是「名下」（advisor-scope.ts）；
  //     团队概览与名册走专门的 advisor:team_view，不经 scope 放大成「全部详情」
  //   - T3 读接口正文泄露核查（设计文档 D11；结论见 project-docs/specs/2026-10-15-ops-lead-read-audit.md）后又收回三个读点位，
  //     因为它们守的 GET 接口会带出原文或「通往原文的凭据」，而这些接口对 operator / support / observer 是现有行为、不能为 ops_lead 改动：
  //       ai_task:read —— GET /ai-tasks、/ai-tasks/:id 原样返回 input_ref / output_ref（含章节全文、用户原话、AI 访谈话术）
  //       content:read —— GET /chapter-rollback/chapters/:id 返回章节正文 body 与历史快照正文
  //       share:read   —— GET /shares、/shares/:id 返回 shareToken，凭它可直接打开公开接口 GET /api/shares/:token 读到章节全文
  //     ai_task:retry / ai_task:cancel / share:revoke 随之一并收回：响应虽不含正文，但它们的页面入口要靠上面被收回的读点位，
  //     ops_lead 已没有入口；留着只会变成「看不见列表却能按 ID 盲调」的孤儿写权限（重试会产生模型费用），按最小权限不给
  //   这个数组被 tests/unit/ops-lead-role.test.ts 用显式白名单逐项断言：增减任何点位都会让测试失败，
  //   必须连同设计文档一起改。
  ops_lead: [
    P.ORDER_READ, P.ORDER_UPDATE_LOGISTICS,
    P.PACKAGE_READ,
    P.BOOST_READ, P.BOOST_READ_RELATIONSHIPS, P.BOOST_FREEZE, P.BOOST_AUDIT,
    P.VOUCHER_READ, P.VOUCHER_REVOKE, P.VOUCHER_REQUEST,
    P.USER_READ, P.PII_READ_PHONE, P.PII_READ_ADDRESS,
    P.CONFIG_READ,
    P.DASHBOARD_READ,
    P.TICKET_READ, P.TICKET_HANDLE,
    P.PARTNER_READ, P.COMMISSION_READ, P.SETTLEMENT_READ,
    P.PARTNER_CREATE, P.PARTNER_STOP,
    P.SALES_ACCOUNT_MANAGE,
    // 名下客户（与 sales 同）
    P.ADVISOR_CUSTOMER_READ, P.ADVISOR_CUSTOMER_WRITE, P.ADVISOR_ORDER_CREATE,
    P.ADVISOR_FULFILL, P.ADVISOR_CONTENT_READ,
    // 团队概览 + 受约束的转交
    P.ADVISOR_TEAM_VIEW, P.ADVISOR_TRANSFER,
  ],
};
